// Оповещения сопровождению (002: contracts/alerts.md; data-model.md, observability.alerts,
// observability.alert_deliveries; FR-039 – FR-045). В подробностях — только поля вида: тип задачи,
// код ошибки, номер операции, числа и время, без персональных данных (FR-041).
import { z } from '../kernel/zod.ts';
import { ErrorCodeSchema } from '../errors/catalog.ts';
import { JobTypeSchema } from '../jobs/catalog.ts';
import { IdSchema, OperationIdSchema, TimestampSchema } from '../kernel/common.ts';

export const alertKinds = [
  'job.failed',
  'worker.no_pulse',
  'backup.stale',
  'backup.failed',
  'backup.disk_low',
  'restore_check.failed',
  'server.errors',
] as const;
export type AlertKind = (typeof alertKinds)[number];
export const AlertKindSchema = z.enum(alertKinds);

/** Сбой словами — дословно из contracts/alerts.md; {подпись типа} — подпись из каталога задач. */
export const alertKindLabels = {
  'job.failed': 'Упала задача «{подпись типа}»',
  'worker.no_pulse': 'Обработчик задач не отвечает',
  'backup.stale': 'Копия базы устарела',
  'backup.failed': 'Копия базы не сделана',
  'backup.disk_low': 'Мало места на диске копий',
  'restore_check.failed': 'Проверка восстановления не прошла',
  'server.errors': 'Частые сбои сервера',
} as const satisfies Record<AlertKind, string>;

/** firing — действует, resolved — закрыто. */
export const alertStates = ['firing', 'resolved'] as const;
export type AlertState = (typeof alertStates)[number];
export const AlertStateSchema = z.enum(alertStates);

export const alertChannels = ['telegram', 'email'] as const;
export type AlertChannel = (typeof alertChannels)[number];
export const AlertChannelSchema = z.enum(alertChannels);

/** fire — тревога, repeat — повтор, resolve — отбой. */
export const deliveryKinds = ['fire', 'repeat', 'resolve'] as const;
export type DeliveryKind = (typeof deliveryKinds)[number];
export const DeliveryKindSchema = z.enum(deliveryKinds);

/** pending — ждёт отправки или повтора, sent — доставлено, failed — после 10 попыток. */
export const deliveryStates = ['pending', 'sent', 'failed'] as const;
export type DeliveryState = (typeof deliveryStates)[number];
export const DeliveryStateSchema = z.enum(deliveryStates);

/** Первая строка ответа канала или итога копии — не длиннее 300 знаков (FR-005а). */
const FirstLineSchema = z.string().max(300);
const CountSchema = z.number().int().nonnegative();

/**
 * Тип упавшей задачи: из каталога или из другой версии сервиса — как есть, подпись — сам тип.
 * Тип — имя из кода вида «модуль.действие»: строчные латинские буквы, цифры, «_» и «-» через
 * точку, без текста и персональных данных (FR-041).
 */
export const AlertJobTypeSchema = z.union([
  JobTypeSchema,
  z
    .string()
    .max(100)
    .regex(/^[a-z][a-z0-9_-]*(?:\.[a-z0-9_-]+)+$/),
]);

/** Подробности по видам сбоя (contracts/alerts.md, «Подробности»). */
export const alertDetailSchemas = {
  'job.failed': z.strictObject({
    jobType: AlertJobTypeSchema,
    code: ErrorCodeSchema,
    operation: OperationIdSchema,
    /** Упавших задач типа сейчас. */
    failed: CountSchema,
  }),
  'worker.no_pulse': z.strictObject({ lastPulseAt: TimestampSchema.nullable() }),
  'backup.stale': z.strictObject({ lastOkAt: TimestampSchema.nullable() }),
  'backup.failed': z.strictObject({ reason: FirstLineSchema }),
  'backup.disk_low': z.strictObject({
    /** Свободно на диске копий по последнему итогу копии и порог alerts.backupDiskMinFreeGb. */
    freeBytes: CountSchema,
    minFreeBytes: CountSchema,
  }),
  'restore_check.failed': z.strictObject({ reason: FirstLineSchema }),
  'server.errors': z.strictObject({
    /** Ответов со сбоем сервера и всех ответов за окно. */
    errors: CountSchema,
    total: CountSchema,
    windowMinutes: z.number().int().positive(),
    /** Последняя операция со сбоем. */
    operation: OperationIdSchema.optional(),
  }),
} as const satisfies Record<AlertKind, z.ZodObject>;

export type AlertDetails<K extends AlertKind> = z.infer<(typeof alertDetailSchemas)[K]>;

export const AlertDeliverySchema = z.object({
  channel: AlertChannelSchema,
  kind: DeliveryKindSchema,
  state: DeliveryStateSchema,
  attempts: CountSchema,
  sentAt: TimestampSchema.optional(),
  /** Первая строка ответа канала — без токена бота и пароля почты (FR-044е). */
  lastError: FirstLineSchema.optional(),
});
export type AlertDelivery = z.infer<typeof AlertDeliverySchema>;

const alertFields = {
  id: IdSchema,
  /** Сбой словами: подпись вида, у упавшей задачи — с подписью типа. */
  kindLabel: z.string(),
  state: AlertStateSchema,
  startedAt: TimestampSchema,
  lastSeenAt: TimestampSchema,
  resolvedAt: TimestampSchema.optional(),
  /** Число случаев с начала. */
  occurrences: z.number().int().positive(),
  deliveries: z.array(AlertDeliverySchema),
};

export const AlertSchema = z.discriminatedUnion('kind', [
  z.object({
    ...alertFields,
    kind: z.literal('job.failed'),
    details: alertDetailSchemas['job.failed'],
  }),
  z.object({
    ...alertFields,
    kind: z.literal('worker.no_pulse'),
    details: alertDetailSchemas['worker.no_pulse'],
  }),
  z.object({
    ...alertFields,
    kind: z.literal('backup.stale'),
    details: alertDetailSchemas['backup.stale'],
  }),
  z.object({
    ...alertFields,
    kind: z.literal('backup.failed'),
    details: alertDetailSchemas['backup.failed'],
  }),
  z.object({
    ...alertFields,
    kind: z.literal('backup.disk_low'),
    details: alertDetailSchemas['backup.disk_low'],
  }),
  z.object({
    ...alertFields,
    kind: z.literal('restore_check.failed'),
    details: alertDetailSchemas['restore_check.failed'],
  }),
  z.object({
    ...alertFields,
    kind: z.literal('server.errors'),
    details: alertDetailSchemas['server.errors'],
  }),
]);
export type Alert = z.infer<typeof AlertSchema>;
